import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { PromiseStatuses } from "../../lib/utilis"
import { InvoiceAmountCreateDTO, InvoiceAmountDTO, InvoiceAmountFilterDTO, InvoiceAmountFindAllResponseDTO, InvoiceAmountUpsertDTO, PartitionFiltersDTO, PartitionFindAllResponseDTO } from "./dto"
import { NewInvoiceAmountService } from "./service"

interface InvoiceAmountState {
    findAllInvoiceAmountResponse?: InvoiceAmountFindAllResponseDTO
    findAllInvoiceAmountStatus: PromiseStatuses
    invoiceAmountFilters: InvoiceAmountFilterDTO
    createInvoiceAmountStatus: PromiseStatuses
    createInvoiceAmountRequest: InvoiceAmountCreateDTO
    findByIdInvoiceAmountStatus: PromiseStatuses
    findByIdInvoiceAmountResponse?: InvoiceAmountDTO
    openNewInvoiceAmountModal: boolean
    editInvoiceAmountRequest: InvoiceAmountUpsertDTO
    editInvoiceAmountStatus: PromiseStatuses
    recoverPartitionMetricsStatus: PromiseStatuses
    partitionPartitionMetricsStatus: PromiseStatuses
    findAllPartitionStatus: PromiseStatuses
    findAllPartitionResponse?: PartitionFindAllResponseDTO
    partitionFilters: PartitionFiltersDTO
}

const initialState: InvoiceAmountState = {
    findAllInvoiceAmountStatus: "idle",
    invoiceAmountFilters: {
        pageSize: 0,
        sortBy: "",
        page: 0,
        sort: 0
    },
    createInvoiceAmountStatus: "idle",
    createInvoiceAmountRequest: {
        month: 0,
        year: 0,
        amount_cents: 0
    },
    findByIdInvoiceAmountStatus: "idle",
    openNewInvoiceAmountModal: false,
    editInvoiceAmountStatus: "idle",
    editInvoiceAmountRequest: {
        month: 0,
        year: 0,
        amount_cents: 0
    },
    recoverPartitionMetricsStatus: "idle",
    partitionPartitionMetricsStatus: "idle",
    findAllPartitionStatus: "idle",
    partitionFilters: {
        month: 0,
        year: 0,
        pageSize: 0,
        sortBy: "",
        page: 0,
        sort: 0
    }
}

export const findAllInvoiceAmount = createAsyncThunk(
    'invoice/findAllInvoiceAmount',
    async (filters: InvoiceAmountFilterDTO, thunkApi): Promise<InvoiceAmountFindAllResponseDTO> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.findAllInvoiceAmount(filters)
    }
)

export const createInvoiceAmount = createAsyncThunk(
    'invoice/createInvoiceAmount',
    async (body: InvoiceAmountCreateDTO, thunkApi): Promise<string> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.createInvoiceAmount(body)
    }
)

export const findByIdInvoiceAmount = createAsyncThunk(
    'invoice/findByIdInvoiceAmount',
    async (id: string, thunkApi): Promise<InvoiceAmountDTO> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.findByIdInvoiceAmount(id)
    }
)

export const editInvoiceAmount = createAsyncThunk(
    'invoice/editInvoiceAmount',
    async (request: { id: string, body: InvoiceAmountUpsertDTO }, thunkApi): Promise<void> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.editInvoiceAmount(request.id, request.body)
    }
)

export const recoverPartitionMetrics = createAsyncThunk(
    'invoice/recoverPartitionMetrics',
    async (): Promise<void> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.recoverPartitionMetrics()
    }
)

export const partitionPartitionMetrics = createAsyncThunk(
    'invoice/partitionPartitionMetrics',
    async (): Promise<void> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.partitionPartitionMetrics()
    }
)

export const findAllPartition = createAsyncThunk(
    'invoice/findAllPartition',
    async (filters: PartitionFiltersDTO): Promise<PartitionFindAllResponseDTO> => {
        const invoiceAmountService = NewInvoiceAmountService()

        return invoiceAmountService.findAllPartition(filters)
    }
)

const invoiceAmountSlice = createSlice({
    name: 'invoice/slice',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(findAllInvoiceAmount.pending, (state) => {
                state.findAllInvoiceAmountStatus = 'loading'
            })
            .addCase(findAllInvoiceAmount.fulfilled, (state, action) => {
                state.findAllInvoiceAmountStatus = 'successfully'
                state.findAllInvoiceAmountResponse = action.payload
            })
            .addCase(findAllInvoiceAmount.rejected, (state) => {
                state.findAllInvoiceAmountStatus = 'failed'
            })
            .addCase(createInvoiceAmount.pending, (state) => {
                state.createInvoiceAmountStatus = 'loading'
            })
            .addCase(createInvoiceAmount.fulfilled, (state, action) => {
                state.createInvoiceAmountStatus = 'successfully'
            })
            .addCase(createInvoiceAmount.rejected, (state) => {
                state.createInvoiceAmountStatus = 'failed'
            })
            .addCase(findByIdInvoiceAmount.pending, (state) => {
                state.findByIdInvoiceAmountStatus = 'loading'
            })
            .addCase(findByIdInvoiceAmount.fulfilled, (state, action) => {
                state.findByIdInvoiceAmountStatus = 'successfully'
                state.findByIdInvoiceAmountResponse = action.payload
            })
            .addCase(findByIdInvoiceAmount.rejected, (state) => {
                state.findByIdInvoiceAmountStatus = 'failed'
            })
            .addCase(editInvoiceAmount.pending, (state) => {
                state.editInvoiceAmountStatus = 'loading'
            })
            .addCase(editInvoiceAmount.fulfilled, (state, action) => {
                state.editInvoiceAmountStatus = 'successfully'
            })
            .addCase(editInvoiceAmount.rejected, (state) => {
                state.editInvoiceAmountStatus = 'failed'
            })
            .addCase(recoverPartitionMetrics.pending, (state) => {
                state.recoverPartitionMetricsStatus = 'loading'
            })
            .addCase(recoverPartitionMetrics.fulfilled, (state, action) => {
                state.recoverPartitionMetricsStatus = 'successfully'
            })
            .addCase(recoverPartitionMetrics.rejected, (state) => {
                state.recoverPartitionMetricsStatus = 'failed'
            })
            .addCase(partitionPartitionMetrics.pending, (state) => {
                state.partitionPartitionMetricsStatus = 'loading'
            })
            .addCase(partitionPartitionMetrics.fulfilled, (state, action) => {
                state.partitionPartitionMetricsStatus = 'successfully'
            })
            .addCase(partitionPartitionMetrics.rejected, (state) => {
                state.partitionPartitionMetricsStatus = 'failed'
            })
            .addCase(findAllPartition.pending, (state) => {
                state.findAllPartitionStatus = 'loading'
            })
            .addCase(findAllPartition.fulfilled, (state, action) => {
                state.findAllPartitionStatus = 'successfully'
                state.findAllPartitionResponse = action.payload
            })
            .addCase(findAllPartition.rejected, (state) => {
                state.findAllPartitionStatus = 'failed'
            })
    },
    reducers: {
        setInvoiceAmountFilterPage: (state, action) => {
            state.invoiceAmountFilters.page = action.payload
        },
        setPartitionFilterPage: (state, action) => {
            state.partitionFilters.page = action.payload
        },
        setOpenNewInvoiceAmountModal: (state, action) => {
            state.openNewInvoiceAmountModal = action.payload
        },
        setCreateInvoiceAmountStatus: (state, action) => {
            state.createInvoiceAmountStatus = action.payload
        },
        setCreateInvoiceAmountCents: (state, action) => {
            state.createInvoiceAmountRequest.amount_cents = action.payload
        },
        setUpsertInvoiceAmountCerts: (state, action) => {
            state.editInvoiceAmountRequest.amount_cents = action.payload
        },
        setEditInvoiceAmountStatus: (state, action) => {
            state.editInvoiceAmountStatus = action.payload
        },
        setPartitionPartitionMetricsStatus: (state, action) => {
            state.partitionPartitionMetricsStatus = action.payload
        },
        setRecoverPartitionMetricsStatus: (state, action) => {
            state.recoverPartitionMetricsStatus = action.payload
        }
    },
})

export const {
    setInvoiceAmountFilterPage,
    setOpenNewInvoiceAmountModal,
    setCreateInvoiceAmountStatus,
    setCreateInvoiceAmountCents,
    setUpsertInvoiceAmountCerts,
    setEditInvoiceAmountStatus,
    setPartitionFilterPage,
    setPartitionPartitionMetricsStatus,
    setRecoverPartitionMetricsStatus
} = invoiceAmountSlice.actions

export default invoiceAmountSlice.reducer